import { isNullOrUndefined } from "./coreUtility";

/** create a new array of a single value, or from another array of values */
export function arrayFromValue<T>(value: T | T[]): T[] {
	if (isNullOrUndefined(value)) {
		return [];
	} else if (Array.isArray(value)) {
		return [...value];
	}

	return [value];
}

/**
 * Sort the array by property, this method will sort the array in place
 * @param lastItem if specified, then only return false if last item duplicates another earlier in the array;
 * this is useful when entering data and only want last entered value to signal an error
 */
export function arrayIsUnique<T, X>(array: T[], valueFunction: (item: T) => X, lastItem?: T): boolean {
	const set = new Set<X>();

	return !!array?.every((item) => {
		const value = valueFunction(item);

		if (set.has(value) && (!lastItem || (item === lastItem))) {
			return false;
		}

		set.add(value);
		return true;
	});
}

/** return last element of an array */
export function arrayLast(array: any[]): any | undefined {
	return array?.length ? array[array.length - 1] : undefined;
}

/** move item from one index to another in the given array, or a copy of the array if copy passed */
export function arrayMove(array: any[], from: number, to: number, copy = false): any[] {
	const { length } = array;

	if ((from >= 0) && (to >= 0) && (from < length) && (to < length)) {
		const result = copy ? array.slice() : array;
		const value = array[from];
		result.splice(from, 1);
		result.splice(to, 0, value);
		return result;
	}

	return array;
}

/**
 * Sort the array by property, this method will sort the array in place
 * @param array the array to be sorted
 * @param property property of each item to sort by
 */
export function sortByString(array: any[], property: string): void {
	if (array?.length > 0 && array[0][property]) {
		array.sort((a, b) => a[property].localeCompare(b[property]));
	}
}

/**
 * Return the difference between the two sets (a - b)
 */
export function setDifference<T>(a: Set<T>, b: Set<T>): T[] {
	const diff: T[] = [];

	for (const x of a) {
		if (!b.has(x)) {
			diff.push(x);
		}
	}

	return diff;
}

/** Splice items from array based on a provided filter function */
export const spliceBy = <T>(data: T[], filterFn: (item: T) => boolean): void => {
	for (let i = data.length - 1; i >= 0; i--) {
		if (filterFn(data[i])) {
			data.splice(i, 1);
		}
	}
};
